import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, Phone, MapPin, Clock, Send, CheckCircle, 
  Building, Calendar, Users, HelpCircle, Briefcase,
  TrendingUp, Radio, Globe, Shield, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Departments
const DEPARTMENTS = [
  { 
    id: 'general', 
    name: 'General Inquiries', 
    email: 'info@michezonet.co.ke',
    icon: HelpCircle,
    description: 'General questions and information'
  },
  { 
    id: 'sales', 
    name: 'Sales & Partnerships', 
    email: 'partnerships@michezonet.co.ke',
    icon: TrendingUp,
    description: 'Sponsorship, partnerships, and commercial inquiries'
  },
  { 
    id: 'technology', 
    name: 'Technology & Platform', 
    email: 'tech@michezonet.co.ke',
    icon: Building,
    description: 'MyGolfHub, platform demos, and tech support'
  },
  { 
    id: 'events', 
    name: 'Events & Operations', 
    email: 'events@michezonet.co.ke',
    icon: Calendar,
    description: 'Tournament management and event inquiries'
  },
  { 
    id: 'golf', 
    name: 'Golf & Road to SomaBay', 
    email: 'golf@michezonet.co.ke',
    icon: Globe,
    description: 'Golf tourism and Road to SomaBay inquiries'
  },
  { 
    id: 'media', 
    name: 'Media & Communications', 
    email: 'media@michezonet.co.ke',
    icon: Radio,
    description: 'Press inquiries and media partnerships'
  },
  { 
    id: 'governance', 
    name: 'Governance & Advisory', 
    email: 'advisory@michezonet.co.ke',
    icon: Shield,
    description: 'Consulting and governance services'
  },
  { 
    id: 'careers', 
    name: 'Careers & Recruitment', 
    email: 'careers@michezonet.co.ke',
    icon: Briefcase,
    description: 'Job opportunities and recruitment'
  }
];

// Inquiry Types
const INQUIRY_TYPES = [
  { id: 'general', name: 'General Inquiry' },
  { id: 'proposal', name: 'Request Proposal' },
  { id: 'demo', name: 'Request Demo' },
  { id: 'consultation', name: 'Book Consultation' },
  { id: 'partnership', name: 'Partnership Opportunity' },
  { id: 'support', name: 'Technical Support' },
  { id: 'feedback', name: 'Feedback' },
  { id: 'press', name: 'Press/Media Inquiry' }
];

const ContactPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('general');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    department: 'general',
    inquiry_type: 'general',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'department') {
      setSelectedDepartment(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/cms/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const currentDept = DEPARTMENTS.find(d => d.id === selectedDepartment);

  return (
    <div className="min-h-screen bg-black">
      <MegaMenu />
      
      {/* Hero Section */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#008000]/10 via-black to-black" />
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-4 py-2 bg-[#008000]/20 border border-[#008000]/30 rounded-full text-[#00CC00] text-sm font-medium mb-6">
              Get in Touch
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
              Let's Work{' '}
              <span className="bg-gradient-to-r from-[#008000] to-[#00CC00] bg-clip-text text-transparent">
                Together
              </span>
            </h1>
            
            <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto">
              Have a question, need a proposal, or want to explore partnership opportunities? 
              Choose the right department and we'll connect you with the right team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Department Selection */}
      <section className="py-12 bg-gray-900/50 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-heading font-bold text-white mb-6 text-center">Select Department</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {DEPARTMENTS.map((dept) => (
              <button
                key={dept.id}
                type="button"
                onClick={() => {
                  setSelectedDepartment(dept.id);
                  setFormData(prev => ({ ...prev, department: dept.id }));
                }}
                className={`p-4 rounded-xl border text-left transition-all ${
                  selectedDepartment === dept.id
                    ? 'bg-[#008000]/20 border-[#008000] text-white'
                    : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30'
                }`}
              >
                <dept.icon className={`w-6 h-6 mb-2 ${selectedDepartment === dept.id ? 'text-[#00CC00]' : 'text-white/40'}`} />
                <p className="font-medium text-sm">{dept.name}</p>
                <p className="text-xs text-white/40 mt-1 hidden sm:block">{dept.description}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl font-heading font-bold text-white mb-6">Contact Information</h2>
                
                {/* Selected Department Info */}
                <div className="bg-[#008000]/10 border border-[#008000]/30 rounded-xl p-6 mb-8">
                  <div className="flex items-center gap-3 mb-3">
                    {currentDept && <currentDept.icon className="w-6 h-6 text-[#00CC00]" />}
                    <h3 className="text-white font-semibold">{currentDept?.name}</h3>
                  </div>
                  <p className="text-white/60 text-sm mb-3">{currentDept?.description}</p>
                  <a 
                    href={`mailto:${currentDept?.email}`}
                    className="text-[#00CC00] text-sm font-medium hover:underline"
                  >
                    {currentDept?.email}
                  </a>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#008000]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-[#008000]" />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Main Email</p>
                      <a href="mailto:info@michezonet.co.ke" className="text-white/60 hover:text-[#008000] transition-colors">
                        info@michezonet.co.ke
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#008000]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-[#008000]" />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Phone</p>
                      <a href="tel:+254700000000" className="text-white/60 hover:text-[#008000] transition-colors">
                        +254 700 000 000
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#008000]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#008000]" />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Location</p>
                      <p className="text-white/60">Nairobi, Kenya</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#008000]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-[#008000]" />
                    </div>
                    <div>
                      <p className="text-white font-medium mb-1">Business Hours</p>
                      <p className="text-white/60">Mon - Fri: 9:00 AM - 6:00 PM (EAT)</p>
                    </div>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="mt-10 pt-10 border-t border-white/10">
                  <h3 className="text-lg font-heading font-semibold text-white mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <a href="/mygolfhub" className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-lg text-white/70 hover:text-[#008000] hover:border-[#008000]/50 transition-all text-sm group">
                      <span>Request Platform Demo</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                    <a href="/somabay" className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-lg text-white/70 hover:text-[#008000] hover:border-[#008000]/50 transition-all text-sm group">
                      <span>Road to SomaBay Info</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                    <a href="/services" className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-lg text-white/70 hover:text-[#008000] hover:border-[#008000]/50 transition-all text-sm group">
                      <span>View Our Services</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white/5 border border-white/10 rounded-2xl p-8"
              >
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-[#008000]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-[#00CC00]" />
                    </div>
                    <h3 className="text-2xl font-heading font-bold text-white mb-3">Message Sent!</h3>
                    <p className="text-white/60 mb-6">
                      Thank you for reaching out to our <span className="text-[#00CC00]">{currentDept?.name}</span> team. 
                      We'll get back to you within 24-48 hours.
                    </p>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({
                          name: '',
                          email: '',
                          phone: '',
                          organization: '',
                          department: 'general',
                          inquiry_type: 'general',
                          subject: '',
                          message: ''
                        });
                      }}
                      className="px-6 py-3 bg-[#008000] hover:bg-[#006600] text-white font-semibold rounded-lg transition-colors"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white/70 text-sm font-medium mb-2">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#008000] transition-colors"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm font-medium mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#008000] transition-colors"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white/70 text-sm font-medium mb-2">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#008000] transition-colors"
                          placeholder="+254 700 000 000"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm font-medium mb-2">Organization</label>
                        <input
                          type="text"
                          name="organization"
                          value={formData.organization}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#008000] transition-colors"
                          placeholder="Your organization name"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white/70 text-sm font-medium mb-2">Department *</label>
                        <select
                          name="department"
                          value={formData.department}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#008000] transition-colors"
                        >
                          {DEPARTMENTS.map((dept) => (
                            <option key={dept.id} value={dept.id} className="bg-gray-900">
                              {dept.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm font-medium mb-2">Inquiry Type *</label>
                        <select
                          name="inquiry_type"
                          value={formData.inquiry_type}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#008000] transition-colors"
                        >
                          {INQUIRY_TYPES.map((type) => (
                            <option key={type.id} value={type.id} className="bg-gray-900">
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-white/70 text-sm font-medium mb-2">Subject *</label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#008000] transition-colors"
                        placeholder="Brief subject of your inquiry"
                      />
                    </div>

                    <div>
                      <label className="block text-white/70 text-sm font-medium mb-2">Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-[#008000] transition-colors resize-none"
                        placeholder="Tell us more about your inquiry..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full px-8 py-4 bg-[#008000] hover:bg-[#006600] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
